import React, { useEffect, useState } from "react";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Container,
  FormFeedback,
  Form,
  Button,
} from "reactstrap";
import { useFormik } from "formik";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { useNavigate, useParams } from "react-router-dom";
import ResultHandler from "../../../components/ResultHandler";
import FormButtonHandler from "../../../components/FormButtonHandler";
import { useResponse } from "../../../hooks/useResponse";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { editLog, getLog, deleteLogFile } from "../../../api/log";

const EditLog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const dealId = localStorage.getItem("dealId");
  const [existingFiles, setExistingFiles] = useState([]);
  const [newFiles, setNewFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);

  const validationSchema = Yup.object().shape({
    delivered: Yup.boolean(),
    pending: Yup.boolean(),
    notes: Yup.string()
      .required(t("Notes are required"))
      .max(500, t("Too long")),
    files: Yup.mixed().nullable(),
  });

  const { setData, loading, error, success, data } = useResponse(
    editLog,
    () => {
      navigate(`/deals/show/${dealId}`);
    }
  );

  const validation = useFormik({
    initialValues: {
      delivered: false,
      pending: false,
      notes: "",
      files: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      // First delete any files marked for deletion sequentially
      if (filesToDelete.length > 0) {
        setIsDeleting(true);
        try {
          // Delete files one by one instead of using Promise.all
          for (const fileUrl of filesToDelete) {
            await deleteLogFile(id, fileUrl);
          }
        } catch (err) {
          console.error("Error deleting files", err);
          // Handle error if needed
        } finally {
          setIsDeleting(false);
        }
      }

      // Then submit the form with remaining files
      const formData = new FormData();
      formData.append("delivered", String(values.delivered));
      formData.append("pending", String(values.pending));
      formData.append("notes", values.notes);

      // Add newly uploaded files
      if (values.files) {
        for (let i = 0; i < values.files.length; i++) {
          formData.append("files", values.files[i]);
        }
      }

      setData({ id, data: formData, type: "edit" });
    },
    enableReinitialize: true,
  });

  const handleDeleteFile = async (fileUrl) => {
    if (existingFiles.includes(fileUrl)) {
      // For existing files, mark for deletion
      setFilesToDelete((prev) => [...prev, fileUrl]);
      setExistingFiles((prev) => prev.filter((file) => file !== fileUrl));
    } else {
      // For new files that haven't been uploaded yet, just remove from state
      setNewFiles((prev) => prev.filter((file) => file.name !== fileUrl));
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setNewFiles((prev) => [...prev, ...files]);
    validation.setFieldValue("files", files);
  };

  useEffect(() => {
    const fetchLog = async () => {
      try {
        if (!id) return;
        const res = await getLog(id);
        const data = res?.data || res;

        if (data) {
          validation.setValues({
            delivered: data.delivered || false,
            pending: data.pending || false,
            notes: data.notes || "",
            files: null,
          });
          setExistingFiles(data.files || []);
        }
      } catch (err) {
        console.error("Failed to fetch log", err);
      }
    };
    fetchLog();
  }, [id]);

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs title={`${t("Edit")} ${t("Log")}`} />

        <Form
          className="needs-validation"
          onSubmit={(e) => {
            e.preventDefault();
            validation.handleSubmit();
          }}
        >
          <Row>
            <Row>
              <Col md="6" className="d-flex align-items-center mb-3">
                <FormGroup check inline>
                  <Input
                    type="checkbox"
                    name="delivered"
                    id="delivered"
                    onChange={validation.handleChange}
                    checked={validation.values.delivered}
                  />
                  <Label for="delivered" check className="mb-0 ms-2">
                    {t("Delivered")}
                  </Label>
                </FormGroup>
              </Col>

              <Col md="6" className="d-flex align-items-center mb-3">
                <FormGroup check inline>
                  <Input
                    type="checkbox"
                    name="pending"
                    id="pending"
                    onChange={validation.handleChange}
                    checked={validation.values.pending}
                  />
                  <Label for="pending" check className="mb-0 ms-2">
                    {t("Pending")}
                  </Label>
                </FormGroup>
              </Col>
            </Row>

            <Col md="12">
              <FormGroup className="mb-3">
                <Label>{t("Notes")}</Label>
                <Input
                  name="notes"
                  placeholder={t("Write any notes")}
                  type="textarea"
                  onChange={validation.handleChange}
                  value={validation.values.notes}
                  invalid={
                    !!(validation.touched.notes && validation.errors.notes)
                  }
                />
                <FormFeedback>{validation.errors.notes}</FormFeedback>
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup className="mb-3">
                <Label>{t("Files")}</Label>
                {existingFiles.length > 0 || newFiles.length > 0 ? (
                  <ul className="list-unstyled">
                    {existingFiles.map((file, index) => (
                      <li
                        key={`existing-${index}`}
                        className="mb-2 d-flex align-items-center"
                      >
                        <a
                          href={file}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="me-2"
                        >
                          {file.split("/").pop()}
                        </a>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteFile(file)}
                          disabled={isDeleting || filesToDelete.includes(file)}
                        >
                          {filesToDelete.includes(file)
                            ? t("Deleting...")
                            : t("Delete")}
                        </Button>
                        {filesToDelete.includes(file) && (
                          <span className="ms-2 text-muted">
                            {t("Processing...")}
                          </span>
                        )}
                      </li>
                    ))}
                    {newFiles.map((file, index) => (
                      <li
                        key={`new-${index}`}
                        className="mb-2 d-flex align-items-center"
                      >
                        <span className="me-2">{file.name}</span>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDeleteFile(file.name)}
                        >
                          {t("Delete")}
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>{t("No files uploaded yet")}</p>
                )}
              </FormGroup>
            </Col>

            <Col md="12">
              <FormGroup className="mb-3">
                <Label>{t("Upload Files")}</Label>
                <Input
                  name="files"
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  invalid={
                    !!(validation.touched.files && validation.errors.files)
                  }
                />
                <FormFeedback>{validation.errors.files}</FormFeedback>
              </FormGroup>
            </Col>
          </Row>

          <FormButtonHandler
            btnTxt={t("Update")}
            loading={loading || isDeleting}
          />
          <ResultHandler
            error={error}
            success={success}
            successMsg={t("Updated Successfully!")}
          />
        </Form>
      </Container>
    </div>
  );
};

export default EditLog;
